/**
 * Theme Models
 * Models for dynamic theming system
 */

export interface ThemeColors {
  primary: string;
  primaryLight: string;
  primaryDark: string;
  secondary: string;
  secondaryLight: string;
  secondaryDark: string;
}

export interface Theme {
  id: string;
  name: string;
  colors: ThemeColors;
  gradient: string;
  gradientHover: string;
  gradientLight: string;
  shadow: string;
}

export interface CompanyTheme {
  companyId: number;
  instituteId: number;
  themeId: string;
  customColors?: ThemeColors;
}

export const PREDEFINED_THEMES: Theme[] = [
  {
    id: 'lavender',
    name: 'Lavender Purple (Default)',
    colors: {
      primary: '#667eea',
      primaryLight: '#8a9af0',
      primaryDark: '#5568d3',
      secondary: '#764ba2',
      secondaryLight: '#9667c0',
      secondaryDark: '#653a8b'
    },
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    gradientHover: 'linear-gradient(135deg, #5568d3 0%, #653a8b 100%)',
    gradientLight: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
    shadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
  },
  {
    id: 'ocean-blue',
    name: 'Ocean Blue',
    colors: {
      primary: '#0ea5e9',
      primaryLight: '#38bdf8',
      primaryDark: '#0284c7',
      secondary: '#0284c7',
      secondaryLight: '#0ea5e9',
      secondaryDark: '#075985'
    },
    gradient: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
    gradientHover: 'linear-gradient(135deg, #0284c7 0%, #075985 100%)',
    gradientLight: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1) 0%, rgba(2, 132, 199, 0.1) 100%)',
    shadow: '0 2px 8px rgba(14, 165, 233, 0.3)'
  },
  {
    id: 'emerald-green',
    name: 'Emerald Green',
    colors: {
      primary: '#10b981',
      primaryLight: '#34d399',
      primaryDark: '#059669',
      secondary: '#047857',
      secondaryLight: '#10b981',
      secondaryDark: '#065f46'
    },
    gradient: 'linear-gradient(135deg, #10b981 0%, #047857 100%)',
    gradientHover: 'linear-gradient(135deg, #059669 0%, #065f46 100%)',
    gradientLight: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(4, 120, 87, 0.1) 100%)',
    shadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
  },
  {
    id: 'sunset-orange',
    name: 'Sunset Orange',
    colors: {
      primary: '#f97316',
      primaryLight: '#fb923c',
      primaryDark: '#ea580c',
      secondary: '#dc2626',
      secondaryLight: '#f97316',
      secondaryDark: '#b91c1c'
    },
    gradient: 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)',
    gradientHover: 'linear-gradient(135deg, #ea580c 0%, #b91c1c 100%)',
    gradientLight: 'linear-gradient(135deg, rgba(249, 115, 22, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
    shadow: '0 2px 8px rgba(249, 115, 22, 0.3)'
  },
  {
    id: 'royal-purple',
    name: 'Royal Purple',
    colors: {
      primary: '#9333ea',
      primaryLight: '#a855f7',
      primaryDark: '#7e22ce',
      secondary: '#7e22ce',
      secondaryLight: '#9333ea',
      secondaryDark: '#6b21a8'
    },
    gradient: 'linear-gradient(135deg, #9333ea 0%, #7e22ce 100%)',
    gradientHover: 'linear-gradient(135deg, #7e22ce 0%, #6b21a8 100%)',
    gradientLight: 'linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(126, 34, 206, 0.1) 100%)',
    shadow: '0 2px 8px rgba(147, 51, 234, 0.3)'
  },
  {
    id: 'rose-pink',
    name: 'Rose Pink',
    colors: {
      primary: '#ec4899',
      primaryLight: '#f472b6',
      primaryDark: '#db2777',
      secondary: '#be185d',
      secondaryLight: '#ec4899',
      secondaryDark: '#9f1239'
    },
    gradient: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
    gradientHover: 'linear-gradient(135deg, #db2777 0%, #9f1239 100%)',
    gradientLight: 'linear-gradient(135deg, rgba(236, 72, 153, 0.1) 0%, rgba(190, 24, 93, 0.1) 100%)',
    shadow: '0 2px 8px rgba(236, 72, 153, 0.3)'
  },
  {
    id: 'teal-cyan',
    name: 'Teal Cyan',
    colors: {
      primary: '#14b8a6',
      primaryLight: '#2dd4bf',
      primaryDark: '#0d9488',
      secondary: '#0891b2',
      secondaryLight: '#14b8a6',
      secondaryDark: '#0e7490'
    },
    gradient: 'linear-gradient(135deg, #14b8a6 0%, #0891b2 100%)',
    gradientHover: 'linear-gradient(135deg, #0d9488 0%, #0e7490 100%)',
    gradientLight: 'linear-gradient(135deg, rgba(20, 184, 166, 0.1) 0%, rgba(8, 145, 178, 0.1) 100%)',
    shadow: '0 2px 8px rgba(20, 184, 166, 0.3)'
  },
  {
    id: 'amber-gold',
    name: 'Amber Gold',
    colors: {
      primary: '#f59e0b',
      primaryLight: '#fbbf24',
      primaryDark: '#d97706',
      secondary: '#d97706',
      secondaryLight: '#f59e0b',
      secondaryDark: '#b45309'
    },
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    gradientHover: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
    gradientLight: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%)',
    shadow: '0 2px 8px rgba(245, 158, 11, 0.3)'
  },
  {
    id: 'slate-blue',
    name: 'Slate Blue',
    colors: {
      primary: '#3b82f6',
      primaryLight: '#60a5fa',
      primaryDark: '#2563eb',
      secondary: '#1e40af',
      secondaryLight: '#3b82f6',
      secondaryDark: '#1e3a8a'
    },
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
    gradientHover: 'linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%)',
    gradientLight: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(30, 64, 175, 0.1) 100%)',
    shadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
  },
  {
    id: 'crimson-red',
    name: 'Crimson Red',
    colors: {
      primary: '#dc2626',
      primaryLight: '#ef4444',
      primaryDark: '#b91c1c',
      secondary: '#991b1b',
      secondaryLight: '#dc2626',
      secondaryDark: '#7f1d1d'
    },
    gradient: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
    gradientHover: 'linear-gradient(135deg, #b91c1c 0%, #7f1d1d 100%)',
    gradientLight: 'linear-gradient(135deg, rgba(220, 38, 38, 0.1) 0%, rgba(153, 27, 27, 0.1) 100%)',
    shadow: '0 2px 8px rgba(220, 38, 38, 0.3)'
  }
];
